import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLoading } from "../../../infra/helpers/alerts";
import { Botao } from "../../../shared/components/Botao";
import { LoadingPage } from "../../../shared/components/LoadingPage";
import { Sidebar } from "../../../shared/components/MenuSidebar/Sidebar";
import { CreateModal } from "../../../shared/components/Modal";
import { ConfirmDeleteModal } from "../../../shared/components/Modal/ConfirmDeleteModal";
import { Navbar } from "../../../shared/components/Navbar";
import { PageHeader } from "../../../shared/components/PageHeader/PageHeader";
import { TableInfo } from "../../../shared/components/Table";
import { IconAdicionar } from "../../../shared/Icons/IconAdicionar";
import { IconTouro } from "../../../shared/Icons/IconTouro";
import { IconVisibility } from "../../../shared/Icons/IconVisibility";
import { SimpleFilter } from "../../../shared/SimpleFilter";
import { FormProduto } from "../components/Form";
import { VisualizarProduto } from "../components/ModalVisualizar";
import useGetProdutos from "../hooks/useGetProdutos";
import usePostProduto from "../hooks/usePostProduto";
import deleteProduto from "../queries/deleteProduto";
import { ProdutoModulo } from "../types/Produto";
import { colunas } from "../util/Colunas";
import Swal from 'sweetalert2'

const pageName = "Produtos";

export function Produtos() {
  const [visible, setVisible] = useState<boolean>(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [valueInput, setValueInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [produtoId, setProdutoId] = useState<number>();
  const [page, setPage] = useState<number>(1);

  const { data: produtosData, isLoading } = useGetProdutos(search, page);

  useLoading(isLoading);

  function handleVisualizar(id?: number) {
    setProdutoId(id);
    setVisible(!visible);
  }

  function handleCloseVisualizar() {
    setProdutoId(undefined);
    setVisible(!visible);
    reset();
  }

  const columns = [
    ...colunas,
    {
      title: "Ações",
      dataIndex: "action",
      key: "action",
      width: 50,
      render: (text: any, record: any, index: any) => {
        return (
          <div className=" flex justify-center gap-3">
            <button
              className="bg-gray-400 hover:bg-primary-hover p-1 text-gray-100 rounded-full"
              onClick={() => handleVisualizar(record.ProdutoId)}
            >
              <IconVisibility width="20" height="20" />
            </button>
          </div>
        );
      },
    },
  ];

  const { register, handleSubmit, reset, formState: { errors} } = useForm<ProdutoModulo>();

  const { mutate: salvarNovoProduto } = usePostProduto(resetForm, () => setIsLoading(false));

  function resetForm() {
    reset()
    setIsLoading(false)
    setVisible(!visible)
  }

  async function onSubmit(data: ProdutoModulo) {
    const dataSave = {...data, ProdutoTipoId: data.ProdutoTipoId ? data.ProdutoTipoId : 1}

    if (navigator.onLine) {
      await salvarNovoProduto(dataSave);
      setIsLoading(true)
    }
    
    !navigator.onLine && Swal.fire({
      title: 'Sem conexão',
      text: 'Por favor, verifique a sua conexão com a internet e tente novamente.',
      icon: 'error',
      position: 'center',
      confirmButtonText: 'Ok',
      confirmButtonColor: '#f27474'
    })

    reset();
  }

  async function onDelete() {
    deleteProduto(produtoId!)
    setVisibleDelete(!visibleDelete)
    setProdutoId(undefined)
  }

  return (
    <div className="flex flex-row mx-auto">
      <div className="flex flex-row">
        <Sidebar />
        <Navbar />
      </div>
      <div className="flex flex-col pl-[10rem] w-[85%] mx-auto">
        <PageHeader 
          titulo={pageName}
          icon={<IconTouro />}
          componente={
            <div className="flex gap-10">
              <SimpleFilter
                placeholder="Pesquisar Produto"
                value={valueInput}
                onChange={(e: any) => setValueInput(e.target.value)}
                onClick={() => {
                  setSearch(valueInput);
                  if(valueInput) {
                    setPage(1);
                  }
                }}
                onCancel={() => {
                  setSearch('')
                  setValueInput('')
                }}
              />
              <Botao 
                texto="Novo" 
                icon={<IconAdicionar color="#fff"/>} 
                onClick={() => setVisible(!visible)} 
                className="bg-primary hover:bg-primary-hover text-white"
              />
            </div>
          }
        />

        {
          produtoId ? <VisualizarProduto 
            idProduto={produtoId} 
            deleteModal={() => {
              handleVisualizar(produtoId)
              setVisibleDelete(!visibleDelete)
            }}
            onCancel={handleCloseVisualizar} 
            visible={visible} 
            errors={errors}
            />
          : <CreateModal
            title="Cadastro de Produto"
            isModalOpen={visible}
            onCancel={() => setVisible(!visible)}
            onSubmit={handleSubmit(onSubmit)}
            closable
            isLoading={loading}
          >
            <FormProduto 
              errors={errors}
              register={register}
            />
          </CreateModal>
        }

        {!produtosData ? (
          <div className="h-[80vh] flex justify-center items-center">
            <LoadingPage />
          </div> 
        ) : (
          <TableInfo
            dataSource={produtosData?.items ?? []}
            columns={columns}
            pagination={{
              current: page,
              pageSize: produtosData?.meta?.itemsPerPage,
              total: produtosData?.meta?.totalItems,
              onChange(p) {
                setPage(p);
              },
            }}
          />
        )}

        <ConfirmDeleteModal
          isModalOpen={visibleDelete}
          title="Deletar Produto"
          mensagem="Você tem certeza que desejar excluir este produto?"
          onDelete={onDelete}
          closeModal={() => {
            setVisibleDelete(!visibleDelete)
            setVisible(!visible);
            setProdutoId(undefined);
          }}
        />
      </div>
    </div>
  );
}
